/* Bloodline motion: scroll reveals, split headings, counters, heartbeat (ECG) lines,
   desktop tilt/parallax. No dependencies. Effects are switched on by classes that
   snippets/bl-assets.liquid puts on <html> from Theme settings > Bloodline animations. */
(function () {
  if (window.BLMotion) return;

  var html = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var fx = function (name) { return html.classList.contains('bl-fx-' + name) && !reduced; };

  /* ---------- Reveal on scroll ---------- */
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      reveal(e.target);
      revealIO.unobserve(e.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }) : null;

  function reveal(el) {
    el.classList.add('is-in');
    /* swipe rails: reveal the whole row at once so cards swiped in later are already visible */
    var rail = el.parentElement;
    if (rail && revealIO && /auto|scroll/.test(getComputedStyle(rail).overflowX)) {
      Array.prototype.forEach.call(rail.children, function (sib) {
        if (sib !== el && sib.classList.contains('bl-reveal') && !sib.classList.contains('is-in')) {
          sib.classList.add('is-in');
          revealIO.unobserve(sib);
        }
      });
    }
    if (el.matches('[data-bl-count]')) countUp(el);
    el.querySelectorAll('[data-bl-count]').forEach(countUp);
  }

  function splitWords(el) {
    if (el.dataset.blSplitDone) return;
    el.dataset.blSplitDone = '1';
    var i = 0;
    var walk = function (node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement('span');
            w.className = 'bl-w';
            var inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--bl-d', (i++ * 0.07).toFixed(2) + 's');
            w.appendChild(inner);
            frag.appendChild(w);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && !child.classList.contains('bl-w')) {
          if (child.children.length || child.matches('.bl-brush, .bl-title__solid, .bl-title__brush, em, strong')) {
            /* keep styled spans whole so masks/rotations stay intact */
            var w2 = document.createElement('span');
            w2.className = 'bl-w';
            child.parentNode.insertBefore(w2, child);
            var inner2 = document.createElement('span');
            inner2.style.setProperty('--bl-d', (i++ * 0.07).toFixed(2) + 's');
            inner2.appendChild(child);
            w2.appendChild(inner2);
          } else {
            walk(child);
          }
        }
      });
    };
    walk(el);
  }

  function countUp(el) {
    if (el.dataset.blCounted) return;
    el.dataset.blCounted = '1';
    var to = parseFloat(el.dataset.blCount);
    var dec = parseInt(el.dataset.blDec || '0', 10);
    if (isNaN(to)) return;
    if (!fx('fade')) { el.textContent = to.toFixed(dec); return; }
    var t0 = performance.now(), dur = 1600;
    var step = function (t) {
      var k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 4);
      el.textContent = (to * e).toFixed(dec);
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function initReveal(root) {
    root.querySelectorAll('[data-bl-stagger]').forEach(function (group) {
      var step = parseFloat(group.dataset.blStagger) || 0.08;
      group.querySelectorAll('.bl-reveal').forEach(function (el, i) {
        if (!el.style.getPropertyValue('--bl-d')) el.style.setProperty('--bl-d', Math.min(i * step, 0.6).toFixed(2) + 's');
      });
    });
    var targets = root.querySelectorAll('.bl-reveal, [data-bl-split], .bl-title, [data-bl-count]');
    if (!fx('fade') || !revealIO) {
      targets.forEach(reveal);
      return;
    }
    root.querySelectorAll('[data-bl-split]').forEach(splitWords);
    root.querySelectorAll('[data-bl-count]').forEach(function (el) {
      if (!el.dataset.blCounted) el.textContent = (0).toFixed(parseInt(el.dataset.blDec || '0', 10));
    });
    targets.forEach(function (el) { revealIO.observe(el); });
  }

  /* ---------- Heartbeat / ECG ----------
     Shape traced from the Bloodline logo: small bump, dip, tall peak, drop, soft hump.
     [x along the beat (0-1), y relative to the peak height (-1 = top)] */
  var BEAT = [[0, 0], [0.097, -0.133], [0.29, 0.446], [0.458, -1], [0.629, 0.145], [0.661, 0.108], [0.903, -0.193], [1, 0]];
  var NS = 'http://www.w3.org/2000/svg';

  function ensurePath(svg, cls) {
    var p = svg.querySelector('.' + cls);
    if (!p) { p = document.createElementNS(NS, 'path'); p.setAttribute('class', cls); svg.appendChild(p); }
    return p;
  }

  function buildEcg(svg) {
    var w = svg.clientWidth || svg.getBoundingClientRect().width;
    var h = svg.clientHeight || svg.getBoundingClientRect().height;
    if (!w || !h) return;
    var beats = (svg.dataset.beats || '0.5').split(',').map(parseFloat).filter(function (n) { return !isNaN(n); }).sort();
    var beatW = Math.min(parseFloat(svg.dataset.beatWidth || '240'), w * 0.42);
    var mid = h * 0.64;
    var amp = h * 0.56 * (parseFloat(svg.dataset.amp || '1'));
    var d = 'M0 ' + mid.toFixed(1);
    var spikes = [];
    var lastX = 0;
    beats.forEach(function (b) {
      var x0 = Math.max(lastX, Math.min(w - beatW, b * w - beatW / 2));
      var sd = 'M' + x0.toFixed(1) + ' ' + mid.toFixed(1);
      d += ' L' + x0.toFixed(1) + ' ' + mid.toFixed(1);
      BEAT.slice(1).forEach(function (pt) {
        var seg = ' L' + (x0 + pt[0] * beatW).toFixed(1) + ' ' + (mid + pt[1] * amp).toFixed(1);
        d += seg; sd += seg;
      });
      spikes.push({ d: sd, at: d });
      lastX = x0 + beatW;
    });
    d += ' L' + w.toFixed(1) + ' ' + mid.toFixed(1);

    svg.setAttribute('viewBox', '0 0 ' + w.toFixed(1) + ' ' + h.toFixed(1));
    svg.setAttribute('preserveAspectRatio', 'none');
    var base = ensurePath(svg, 'bl-ecg__base');
    var pulse = ensurePath(svg, 'bl-ecg__pulse');
    var head = ensurePath(svg, 'bl-ecg__head');
    base.setAttribute('d', d);
    pulse.setAttribute('d', d);
    head.setAttribute('d', d);

    svg.querySelectorAll('.bl-ecg__spike').forEach(function (p) { p.remove(); });
    var L = pulse.getTotalLength();
    var seg = Math.min(L * 0.3, 460);
    var tip = 10;
    var rest = Math.max(L * 0.35, 240);
    var travel = L + rest + seg;
    var dur = Math.max(1.8, Math.min(5.2, travel / 520));
    var measure = ensurePath(svg, 'bl-ecg__measure');
    measure.style.visibility = 'hidden';
    measure.style.stroke = 'none';

    spikes.forEach(function (s) {
      var sp = document.createElementNS(NS, 'path');
      sp.setAttribute('class', 'bl-ecg__spike');
      sp.setAttribute('d', s.d);
      measure.setAttribute('d', s.at);
      var reach = measure.getTotalLength() - (beatW * 0.55);
      var delay = Math.max(0, (reach / travel) * dur - dur * 0.04);
      sp.style.setProperty('--bl-ecg-delay', delay.toFixed(3) + 's');
      svg.insertBefore(sp, pulse);
    });

    pulse.style.strokeDasharray = seg.toFixed(1) + ' ' + (2 * L + seg + rest).toFixed(1);
    pulse.style.setProperty('--bl-from', seg.toFixed(1) + 'px');
    pulse.style.setProperty('--bl-to', (-(L + rest)).toFixed(1) + 'px');
    head.style.strokeDasharray = tip + ' ' + (2 * L + seg + rest).toFixed(1);
    head.style.setProperty('--bl-from', tip + 'px');
    head.style.setProperty('--bl-to', (-(L + rest) - seg + tip).toFixed(1) + 'px');
    svg.style.setProperty('--bl-ecg-dur', dur.toFixed(2) + 's');

    /* restart animations together so the spike flash lines up with the pulse */
    svg.classList.remove('is-ready');
    void svg.getBoundingClientRect();
    svg.classList.add('is-ready');
  }

  var ecgRO = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) {
      var svg = e.target;
      var w = Math.round(e.contentRect.width);
      if (svg._blW === w) return;
      svg._blW = w;
      buildEcg(svg);
    });
  }) : null;

  var ecgIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('is-paused', !e.isIntersecting); });
  }) : null;

  function initEcg(root) {
    root.querySelectorAll('svg[data-bl-ecg]').forEach(function (svg) {
      buildEcg(svg);
      if (ecgRO) ecgRO.observe(svg);
      if (ecgIO) ecgIO.observe(svg);
    });
  }

  /* ---------- Desktop tilt + mouse parallax ---------- */
  function initPointer(root) {
    if (!finePointer || !fx('hover')) return;
    root.querySelectorAll('[data-bl-tilt]').forEach(function (el) {
      if (el._blTilt) return;
      el._blTilt = true;
      var max = parseFloat(el.dataset.blTilt) || 6;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        el.classList.add('is-tilting');
        el.style.setProperty('--bl-rx', (x * max).toFixed(2) + 'deg');
        el.style.setProperty('--bl-ry', (-y * max).toFixed(2) + 'deg');
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-tilting');
        el.style.setProperty('--bl-rx', '0deg');
        el.style.setProperty('--bl-ry', '0deg');
      });
    });
    root.querySelectorAll('[data-bl-parallax]').forEach(function (zone) {
      if (zone._blPar) return;
      zone._blPar = true;
      var items = zone.querySelectorAll('[data-bl-depth]');
      var raf = 0, px = 0, py = 0;
      var apply = function () {
        raf = 0;
        items.forEach(function (it) {
          var d = parseFloat(it.dataset.blDepth) || 10;
          it.style.translate = (px * d).toFixed(1) + 'px ' + (py * d).toFixed(1) + 'px';
        });
      };
      zone.addEventListener('pointermove', function (e) {
        var r = zone.getBoundingClientRect();
        px = (e.clientX - r.left) / r.width - 0.5;
        py = (e.clientY - r.top) / r.height - 0.5;
        if (!raf) raf = requestAnimationFrame(apply);
      });
      zone.addEventListener('pointerleave', function () { px = 0; py = 0; if (!raf) raf = requestAnimationFrame(apply); });
    });
  }

  /* ---------- Scroll parallax: [data-bl-scroll="speed"], [data-bl-scroll-axis="y"] ---------- */
  var scrollItems = [];
  var scrollTicking = false;
  function updateScroll() {
    scrollTicking = false;
    var vh = window.innerHeight;
    scrollItems.forEach(function (el) {
      var zone = el.parentElement;
      if (!zone || !el.isConnected) return;
      var r = zone.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var shift = (r.top + r.height / 2 - vh / 2) * (parseFloat(el.dataset.blScroll) || 0);
      el.style.translate = el.dataset.blScrollAxis === 'y' ? '0 ' + shift.toFixed(1) + 'px' : shift.toFixed(1) + 'px 0';
    });
  }
  function initScroll(root) {
    if (!finePointer || !fx('hover')) return;
    root.querySelectorAll('[data-bl-scroll]').forEach(function (el) {
      if (scrollItems.indexOf(el) === -1) scrollItems.push(el);
    });
    if (scrollItems.length && !initScroll.bound) {
      initScroll.bound = true;
      window.addEventListener('scroll', function () {
        if (!scrollTicking) { scrollTicking = true; requestAnimationFrame(updateScroll); }
      }, { passive: true });
    }
    updateScroll();
  }

  function init(root) {
    root = root || document;
    initReveal(root);
    initEcg(root);
    initPointer(root);
    initScroll(root);
  }

  window.BLMotion = { init: init, buildEcg: buildEcg };
  html.classList.add('bl-motion-ready');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  /* theme editor: re-run for sections that are added or re-rendered */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
